import { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db, storage } from "../config/firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  setDoc,
  doc,
  writeBatch,
  where,
  getDocs,
  getDoc,
  increment,
  updateDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuthState } from 'react-firebase-hooks/auth';
import MessageComposer from '../components/messageComposer';
import SafeMedia from '../components/SafeMedia';
import Modal from 'react-modal';
import defaultAvatar from '../images/default.jpg';
import stealthImage from '../images/stealth-hide.png'
import { useStealthMode } from '../context/StealthModeContext';

// =================================================================
// REFINED MESSAGE COMPONENT
// =================================================================

export default function Message() {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
    const { enabled } = useStealthMode();

  // State for the image to be uploaded
  const [readyFile, setReadyFile] = useState(null);
  const [origBytes, setOrigBytes] = useState(0);
  const [compBytes, setCompBytes] = useState(0);

  // State for controlling the ImagePickerCompressor UI
  const [ipcState, setIpcState] = useState({
    previewUrl: "",
    selectedName: "",
    savingsText: "",
    compressionProgress: 0,
    error: "",
  });

  const [user] = useAuthState(auth);
  const { mid } = useParams();
  const navigate = useNavigate();

  // 🐛 FIX: Use useMemo for stable Firestore references
  const threadRef = useMemo(() => doc(db, 'messages', mid || '_missing'), [mid]);
  const messagesRef = useMemo(() => collection(db, 'messages', mid, 'msg'), [mid]);

  const [messages, setMessages] = useState([]);
  const [uidA, uidB] = useMemo(() => (mid || '').split('_'), [mid]);
  const other = user ? (uidA === user.uid ? uidB : uidA) : null;
  
  const [userByUid, setUserByUid] = useState({});
  const [myIsPremium, setMyIsPremium] = useState(false);
  const [myBlocked, setMyBlocked] = useState([]); // Array to store current user's blocked IDs
  const [hitMsgLimit, setHitMsgLimit] = useState(false); 
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  // Helper for time formatting
  const formatTime = useCallback((ts) => {
    if (!ts) return 'sending…';
    // Use optional chaining to safely check for toDate()
    const d = ts.toDate?.() || new Date(ts);
    return d.toLocaleString();
  }, []);

  // 1. Revoke Blob URL Cleanup
  useEffect(() => {
    return () => {
      if (ipcState.previewUrl) {
        URL.revokeObjectURL(ipcState.previewUrl);
      }
    };
  }, [ipcState.previewUrl]);

  // 2. Load Participants Profiles (Stable UIDs)
  useEffect(() => {
    const loadProfiles = async () => {
      if (!uidA || !uidB) return;
      try {
        const q = query(collection(db, 'users'), where('authUID', 'in', [uidA, uidB]));
        const snap = await getDocs(q);
        const map = {};
        snap.forEach(d => {
          const data = d.data();
          if (data?.authUID) {
            map[data.authUID] = {
              displayName: data.displayName || data.username || data.authUID,
              displayPhoto: data.displayPhoto || data.photoURL || null,
              blocked: Array.isArray(data.blocked) ? data.blocked : [],
            };
          }
        });
        setUserByUid(map);
      } catch (e) {
        console.error('loadProfiles error:', e);
      }
    };
    loadProfiles();
  }, [uidA, uidB]); 

  // 3. Load User Premium Status (Listen to user doc)
  useEffect(() => {
    if (!user) {
      setMyIsPremium(false);
      return;
    }

    let unsubscribe;
    const fetchUserDoc = async () => {
      try {
        // Query to find the user's document by authUID
        const userQuery = query(collection(db, 'users'), where('authUID', '==', user.uid));
        const querySnapshot = await getDocs(userQuery);
        
        if (!querySnapshot.empty) {
          const userDocRef = querySnapshot.docs[0].ref;
          
          // Set up real-time listener on the specific user document
          unsubscribe = onSnapshot(userDocRef, (doc) => {
            if (doc.exists()) {
              const userData = doc.data();
              setMyIsPremium(!!userData.isPremium);
              setMyBlocked(Array.isArray(userData.blocked) ? userData.blocked : []);
            } else {
              setMyIsPremium(false);
              setMyBlocked([]);
            }
          });
          // Return the cleanup function directly
          return unsubscribe;
        }
      } catch (error) {
        console.error("Error fetching user document:", error);
        setMyIsPremium(false);
        setMyBlocked([]);
      }
      // Return a no-op cleanup function if no document was found or an error occurred
      return () => {};
    };

    // Call the async function and rely on its return value for cleanup
    let cleanupPromise = fetchUserDoc();

    return () => {
      // Execute the cleanup function when the effect cleans up
      cleanupPromise.then(unsub => unsub && unsub());
    };
  }, [user]);

  // 4. Initialize/Update Thread Metadata (Stable Ref)
  useEffect(() => {
    if (!uidA || !uidB || !mid) return;
    setDoc(
      threadRef,
      {
        participants: [uidA, uidB],
        createdAt: serverTimestamp(),
      },
      { merge: true }
    ).catch(console.error);
  }, [uidA, uidB, mid, threadRef]); // threadRef is now stable due to useMemo

  // 5. Real-time Message Listener (Stable Ref)
  useEffect(() => {
    if (!mid) return;
    const queryMsgs = query(messagesRef, orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(queryMsgs, (snapshot) => {
      const msgs = [];
      snapshot.forEach((d) => {
        const data = d.data();
        if (!data.deleted) {
          msgs.push({ ...data, id: d.id });
        }
      });
      setMessages(msgs);
    });
    // This is the cleanup function for the listener
    return unsubscribe;
  }, [mid, messagesRef]); // messagesRef is now stable due to useMemo

  // 6. Auto-scroll when messages change
  const scrollToBottom = useCallback(() => {
    // messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, []);

  // const scrollToBottom = useCallback(() => {
  //   messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  // }, []);

  // const scrollToBottom = useCallback(() => {
  //   if (messagesEndRef.current) {
  //     messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
  //   }
  // }, []); // Scroll logic is stable

  useLayoutEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "auto",
    });
  }, [messages]);

  // 7. Mark Messages as Read (Stable Ref)
  useEffect(() => {
    if (!user || !messages.length || !mid) return;

    // Check if the latest message is mine OR if there are no unread messages from 'other'
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === user.uid) return;

    const unreadIds = messages
      .filter(m => m.sender !== user.uid && !m.read)
      .map(m => m.id);

    if (!unreadIds.length) return;

    const batch = writeBatch(db);
    unreadIds.forEach(id => {
      batch.update(doc(db, 'messages', mid, 'msg', id), {
        read: true,
        readAt: serverTimestamp(),
      });
    });
    batch.set(
      threadRef,
      { lastRead: { [user.uid]: serverTimestamp() } },
      { merge: true }
    );
    // Commit the batch to mark messages as read
    batch.commit().catch(console.error);
    // Dependencies: messages (triggers when new messages arrive), user, mid, threadRef (stable)
  }, [messages, user, mid, threadRef]);


  // Helper for image format string
  const fmtBytes = useCallback((bytes) => {
      if (bytes == null) return "";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0, n = bytes;
      while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
      const val = n >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
      return `${val} ${units[i]}`;
  }, []);

  // 8. Image Change Handler
  const handleImageChange = useCallback((data) => {
    if (ipcState.previewUrl) {
      URL.revokeObjectURL(ipcState.previewUrl);
    }

    setReadyFile(data.file || null);
    setOrigBytes(data.originalBytes || 0);
    setCompBytes(data.compressedBytes || 0);

    const savings = (() => {
      if (!data.originalBytes || !data.compressedBytes) return "";
      const before = fmtBytes(data.originalBytes);
      const after = fmtBytes(data.compressedBytes);
      const pct = data.originalBytes ? Math.round(100 - (data.compressedBytes / data.originalBytes) * 100) : 0;
      return `${before} → ${after} (${pct > 0 ? "−" + pct : "0"}%)`;
    })();

    setIpcState({
      previewUrl: data.previewUrl || "",
      selectedName: data.file?.name || "",
      savingsText: savings,
      compressionProgress: data.compressionProgress || 0,
      error: data.error || "",
    });
  }, [ipcState.previewUrl, fmtBytes]);

  // 9. Image Clear Handler
  const handleImageClear = useCallback(() => {
    if (ipcState.previewUrl) {
      URL.revokeObjectURL(ipcState.previewUrl);
    }
    setReadyFile(null);
    setOrigBytes(0);
    setCompBytes(0);
    setIpcState({
      previewUrl: "",
      selectedName: "",
      savingsText: "",
      compressionProgress: 0,
      error: "",
    });
  }, [ipcState.previewUrl]);


  // 10. Image Upload Utility
  const uploadImageAndGetUrl = useCallback((fileToUpload) =>
    new Promise((resolve, reject) => {
      if (!fileToUpload) return resolve(null);
      const ts = Date.now();
      const storageRef = ref(storage, `messages/${mid}/${user.uid}/${ts}_${fileToUpload.name}`);
      const task = uploadBytesResumable(storageRef, fileToUpload, { contentType: fileToUpload.type });

      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setIpcState(s => ({ ...s, compressionProgress: pct })); // Upload progress
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            const imagePath = task.snapshot.ref.fullPath;
            resolve({url, imagePath});
          } catch (e) {
            reject(e);
          }
        }
      );
    }), [mid, user?.uid]); // mid and user.uid are stable or change infrequently


  // 11. Send Message Handler
  const handleSendNewMessage = useCallback(async () => {
    const textToSend = newMessage.trim();
    const fileToUpload = readyFile;
    const originalBytesValue = origBytes;
    const compressedBytesValue = compBytes;

    if (!fileToUpload && !textToSend) {
      return;
    }

    // --- Optimistic UI: Phase 1 ---
    const tempPreviewUrl = ipcState.previewUrl; // Keep preview URL for optimistic display
    
    // Clear input fields immediately.
    setNewMessage('');
    handleImageClear(); // Clears readyFile, ipcState, etc.

    let restoredText = textToSend;
    let messageDocRef = null;

    try {
      // Re-verify block status before sending
      if (isBlocked || theyBlockedMe) {
        setNewMessage(restoredText);
        return;
      }

      // Check daily message limit for non-premium users
      if (!myIsPremium) {
        const todayStr = new Date().toISOString().slice(0, 10);
        const counterRef = doc(db, 'dailyMessageCounters', `${user.uid}_${todayStr}`);
        const snap = await getDoc(counterRef);
        const count = snap.exists() ? snap.data()?.count || 0 : 0;
        if (count >= 20) {
          setHitMsgLimit(true);
          setNewMessage(restoredText); // Restore text
          // Image selection cannot be easily restored after handleImageClear()
          return;
        }
      }

      // Create the message document *immediately* with placeholder content.
      const payload = {
        text: restoredText,
        imageUrl: fileToUpload ? tempPreviewUrl : null, // Use local blob URL for instant display
        imagePath: null,
        sender: user.uid,
        createdAt: serverTimestamp(),
        read: false,
        type: fileToUpload ? 'image' : 'text',
        status: fileToUpload ? 'uploading' : 'sent', // New status field
        originalBytes: originalBytesValue || null,
        compressedBytes: compressedBytesValue || null,
      };

      // Add the doc and get its reference
      messageDocRef = await addDoc(messagesRef, payload);

      // Update thread's last message optimistically
      const lastText = `${fileToUpload ? '[Photo]' : ''}${fileToUpload && restoredText ? ' ' : ''}${restoredText}`;
      await setDoc(
        threadRef,
        {
          lastText: lastText || '[Photo]',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Increment daily counter for non-premium users
      if (!myIsPremium) {
        const todayStr = new Date().toISOString().slice(0, 10);
        const counterRef = doc(db, 'dailyMessageCounters', `${user.uid}_${todayStr}`);
        await setDoc(counterRef, { date: todayStr, count: increment(1) }, { merge: true });
      }

      // --- Optimistic UI: Phase 2 (Image Upload) ---
      if (fileToUpload) {
        try {
          const { url: finalImageUrl, imagePath: finalImagePath } = await uploadImageAndGetUrl(fileToUpload);

          // Once uploaded, update the message document with the real URL and status.
          await updateDoc(messageDocRef, {
            imageUrl: finalImageUrl,
            imagePath: finalImagePath,
            status: 'sent',
          });
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          // If upload fails, update the message to show an error state.
          await updateDoc(messageDocRef, {
            status: 'failed',
            // Note: We use restoredText here, which is safer than textToSend due to scope
            text: `${restoredText} (Image upload failed)`, 
          });
        } finally {
            if (tempPreviewUrl) {
              URL.revokeObjectURL(tempPreviewUrl);
            }
        }
      }
    } catch (e) {
      console.error("An error occurred while sending the message:", e);
      // If the initial message doc creation fails, restore the text.
      setNewMessage(restoredText);
      // NOTE: If messageDocRef was created, you might want to delete it or mark it as 'failed' here too.
    }
  }, [
    newMessage, 
    readyFile, 
    origBytes, 
    compBytes, 
    ipcState.previewUrl, 
    myIsPremium, 
    user?.uid, 
    messagesRef, 
    threadRef, 
    handleImageClear, 
    uploadImageAndGetUrl
  ]);

  // 12. Navigation Handler (Stable Function)
  const handleViewUserProfile = useCallback(() => {
      navigate(`/profile/${other}`);
  }, [navigate, other]);

  // 13. Delete Modal Handlers (Stable Functions)
  const openDeleteModal = useCallback((messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteMessage = useCallback(async () => {
    if (!messageToDelete) return;

    // Optimistically close the modal
    setShowDeleteModal(false);
    const messageIdToDelete = messageToDelete;
    setMessageToDelete(null);

    try {
      const messageRef = doc(db, 'messages', mid, 'msg', messageIdToDelete);
      await updateDoc(messageRef, { deleted: true });
    } catch (error) {
      console.error("Error deleting message: ", error);
      // TODO: Handle deletion error, e.g., show a notification
    }
  }, [messageToDelete, mid]);

  // Determine block states
  const isBlocked = myBlocked?.includes(other);
  const theyBlockedMe = userByUid[other]?.blocked?.includes(user?.uid);

  return (
    <>
      {/* Modal Definitions (Unchanged) */}
      <Modal
        className='upgrade-prompt'
        isOpen={showDeleteModal}
        onRequestClose={() => setShowDeleteModal(false)}
        contentLabel="Delete Message Confirmation"
      >
        <h2>Delete Message</h2>
        <p>Are you sure you want to delete this message? This action cannot be undone.</p>
        <div className="actions">
          <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
          <button onClick={handleDeleteMessage}>Delete</button>
        </div>
      </Modal>
      <Modal
        className='upgrade-prompt'
        isOpen={hitMsgLimit}
        onRequestClose={() => setHitMsgLimit(false)}
        contentLabel="Upgrade to send more messages"
      >
        <h2>Daily message limit reached</h2>
        <p>You’ve sent 20 messages today. Upgrade to Premium to keep chatting without limits.</p>
        <div className="actions">
          <button onClick={() => setHitMsgLimit(false)}>Maybe later</button>
          <button onClick={() => { setHitMsgLimit(false); navigate('/pricing') }}>Upgrade</button>
        </div>
      </Modal>
      
      {/* Main Conversation UI (Unchanged structure) */}
      <div className='main conversation'>
        <div className="recipient-info">
          <img
            className="recipient-photo"
            alt="User avatar"
            src={enabled ?stealthImage : userByUid[other]?.displayPhoto || defaultAvatar}
            style={{ 
              cursor: 'pointer',
              borderRadius: '50%' ,
              height: enabled ? '25px' : '40px',
              width: enabled ? '25px' : '40px',
              padding: enabled ? '7.5px' : '0px',
              objectFit: 'contain',
              backgroundColor: '#24252A'
            }}
            onClick={handleViewUserProfile}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = defaultAvatar;
            }}
          />
          <p className="recipient-name">{userByUid[other]?.displayName || other}</p>
        </div>
        <div className="messages">
        {/* <div className="messages" ref={messagesEndRef}> */}
          {messages.map((msg) => {
            const mine = msg.sender === user?.uid;
            const senderProfile = userByUid[msg.sender];
            const senderName = mine ? 'You' : (senderProfile?.displayName || msg.sender);

            return (
              <div key={msg.id} className={`message ${mine ? 'outgoing' : 'incoming'}`}>
                {myIsPremium && (
                  <button className="delete-message-btn" onClick={() => openDeleteModal(msg.id)}>
                    🗑️
                  </button>
                )}
                {msg.imageUrl && (
                  <div>
                    <a href={msg.imageUrl} target="_blank" rel="noreferrer">
                      <SafeMedia src={msg.imageUrl} alt="sent" className="message-image" onLoad={scrollToBottom} />
                    </a>
                    {msg.expiresAt_sender && msg.sender === user.uid && (
                      <p className="deletion-date">
                        Will be deleted on: {new Date(msg.expiresAt_sender.seconds * 1000).toLocaleDateString()}
                      </p>
                    )}
                    {msg.expiresAt_receiver && msg.sender !== user.uid && (
                      <p className="deletion-date">
                        Will be deleted on: {new Date(msg.expiresAt_receiver.seconds * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
                {/* Security Note: dangerousSetInnerHTML retained for functionality, ensure content is sanitized server-side */}
                {msg.text && <div className="message-text" dangerouslySetInnerHTML={{ __html: msg.text }}></div>}
                <div className="message-meta">
                  <span className="sender-name">{senderName}</span>
                  {mine && (
                    <div className="message-status">
                      {msg.read ? 'Seen' : 'Sent'}
                    </div>
                  )}
                  <time
                    className="timestamp"
                    dateTime={msg.createdAt?.toDate ? msg.createdAt.toDate().toISOString() : undefined}
                    title={formatTime(msg.createdAt)}
                  >
                    {msg.status === 'uploading' && 'Sending...'}
                    {msg.status === 'failed' && 'Failed'}
                    {(!msg.status || msg.status === 'sent') && formatTime(msg.createdAt)}
                  </time>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} className="messagesend" />
        </div>
      </div>

      {/* Composer (Unchanged structure) */}
      {!isBlocked && !theyBlockedMe ? (
        <MessageComposer
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendNewMessage={handleSendNewMessage}
          handleImageChange={handleImageChange}
          ipcState={ipcState}
          handleImageClear={handleImageClear}
        />
      ) : (
        <div className="message-blocked-notice" style={{ padding: '20px', textAlign: 'center', color: '#888', borderTop: '1px solid #eee' }}>
          {isBlocked 
            ? "You have blocked this user. Unblock them to start a conversation." 
            : "This person blocked you. You cannot message him."}
        </div>
      )}
    </>
  );
}

// import { useEffect, useLayoutEffect, useState, useRef } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { auth, db, storage } from "../config/firebase";
// import {
//   addDoc,
//   collection,
//   onSnapshot,
//   serverTimestamp,
//   query,
//   orderBy,
//   setDoc,
//   doc,
//   writeBatch,
//   where,
//   getDocs,
//   getDoc,
//   increment,
//   updateDoc
// } from "firebase/firestore";
// import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
// import { useAuthState } from 'react-firebase-hooks/auth';
// import MessageComposer from '../components/messageComposer';
// import SafeMedia from '../components/SafeMedia';
// import Modal from 'react-modal';

// export default function Message() {
//   const [newMessage, setNewMessage] = useState('');
//   const messagesEndRef = useRef(null);

//   // State for the image to be uploaded
//   const [readyFile, setReadyFile] = useState(null);
//   const [origBytes, setOrigBytes] = useState(0);
//   const [compBytes, setCompBytes] = useState(0);

//   // State for controlling the ImagePickerCompressor UI
//   const [ipcState, setIpcState] = useState({
//     previewUrl: "",
//     selectedName: "",
//     savingsText: "",
//     compressionProgress: 0,
//     error: "",
//   });

//   const [user] = useAuthState(auth);
//   const { mid } = useParams();
//   const navigate = useNavigate();
//   const messagesRef = collection(db, 'messages', mid, 'msg');
//   const [messages, setMessages] = useState([]);
//   const [uidA, uidB] = (mid || '').split('_');
//   const other = user ? (uidA === user.uid ? uidB : uidA) : null;
  
//   const threadRef = doc(db, 'messages', mid || '_missing');
//   const [userByUid, setUserByUid] = useState({});
//   const [myIsPremium, setMyIsPremium] = useState(false);
//   const [hitMsgLimit, setHitMsgLimit] = useState(false); 
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [messageToDelete, setMessageToDelete] = useState(null);

//   useEffect(() => {
//     // Revoke the object URL on unmount to prevent memory leaks
//     return () => {
//       if (ipcState.previewUrl) {
//         URL.revokeObjectURL(ipcState.previewUrl);
//       }
//     };
//   }, [ipcState.previewUrl]);

//   const formatTime = (ts) => {
//     if (!ts) return 'sending…';
//     const d = ts.toDate ? ts.toDate() : new Date(ts);
//     return d.toLocaleString();
//   };

//   useEffect(() => {
//     const loadProfiles = async () => {
//       if (!uidA || !uidB) return;
//       try {
//         const q = query(collection(db, 'users'), where('authUID', 'in', [uidA, uidB]));
//         const snap = await getDocs(q);
//         const map = {};
//         snap.forEach(d => {
//           const data = d.data();
//           if (data?.authUID) {
//             map[data.authUID] = {
//               displayName: data.displayName || data.username || data.authUID,
//               displayPhoto: data.displayPhoto || data.photoURL || null,
//             };
//           }
//         });
//         setUserByUid(map);
//       } catch (e) {
//         console.error('loadProfiles error:', e);
//       }
//     };
//     loadProfiles();
//   }, [uidA, uidB]); 

//   useEffect(() => {
//     if (!user) {
//       setMyIsPremium(false);
//       return;
//     }

//     const fetchUserDoc = async () => {
//       try {
//         const userQuery = query(collection(db, 'users'), where('authUID', '==', user.uid));
//         const querySnapshot = await getDocs(userQuery);
//         if (!querySnapshot.empty) {
//           const userDocRef = querySnapshot.docs[0].ref;
//           const unsubscribe = onSnapshot(userDocRef, (doc) => {
//             if (doc.exists()) {
//               const userData = doc.data();
//               setMyIsPremium(!!userData.isPremium);
//             } else {
//               setMyIsPremium(false);
//             }
//           });
//           return unsubscribe;
//         }
//       } catch (error) {
//         console.error("Error fetching user document:", error);
//         setMyIsPremium(false);
//       }
//     };

//     let unsubscribe;
//     fetchUserDoc().then(unsub => {
//       unsubscribe = unsub;
//     });

//     return () => {
//       if (unsubscribe) {
//         unsubscribe();
//       }
//     };
//   }, [user]);

//   useEffect(() => {
//     if (!uidA || !uidB || !mid) return;
//     setDoc(
//       threadRef,
//       {
//         participants: [uidA, uidB],
//         createdAt: serverTimestamp(),
//       },
//       { merge: true }
//     ).catch(console.error);
//   }, [uidA, uidB, mid, threadRef]);

//   useEffect(() => {
//     if (!mid) return;
//     const queryMsgs = query(messagesRef, orderBy('createdAt', 'asc'));
//     const unsubscribe = onSnapshot(queryMsgs, (snapshot) => {
//       const msgs = [];
//       snapshot.forEach((d) => {
//         const data = d.data();
//         if (!data.deleted) {
//           msgs.push({ ...data, id: d.id });
//         }
//       });
//       setMessages(msgs);
//     });
//     return unsubscribe;
//   }, [mid, messagesRef]);

//   const scrollToBottom = () => {
//     if (messagesEndRef.current) {
//       messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
//     }
//   };

//   useLayoutEffect(() => {
//     scrollToBottom();
//   }, [messages]);

//   useEffect(() => {
//     if (!user || !messages.length || !mid) return;
//     const unreadIds = messages
//       .filter(m => m.sender !== user.uid && !m.read)
//       .map(m => m.id);
//     if (!unreadIds.length) return;

//     const batch = writeBatch(db);
//     unreadIds.forEach(id => {
//       batch.update(doc(db, 'messages', mid, 'msg', id), {
//         read: true,
//         readAt: serverTimestamp(),
//       });
//     });
//     batch.set(
//       threadRef,
//       { lastRead: { [user.uid]: serverTimestamp() } },
//       { merge: true }
//     );
//     batch.commit().catch(console.error);
//   }, [messages, user, mid, threadRef]);


//   const handleImageChange = (data) => {
//     if (ipcState.previewUrl) {
//       URL.revokeObjectURL(ipcState.previewUrl);
//     }

//     setReadyFile(data.file || null);
//     setOrigBytes(data.originalBytes || 0);
//     setCompBytes(data.compressedBytes || 0);

//     const fmtBytes = (bytes) => {
//       if (bytes == null) return "";
//       const units = ["B", "KB", "MB", "GB"];
//       let i = 0, n = bytes;
//       while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
//       const val = n >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
//       return `${val} ${units[i]}`;
//     };

//     const savings = (() => {
//       if (!data.originalBytes || !data.compressedBytes) return "";
//       const before = fmtBytes(data.originalBytes);
//       const after = fmtBytes(data.compressedBytes);
//       const pct = data.originalBytes ? Math.round(100 - (data.compressedBytes / data.originalBytes) * 100) : 0;
//       return `${before} → ${after} (${pct > 0 ? "−" + pct : "0"}%)`;
//     })();

//     setIpcState({
//       previewUrl: data.previewUrl || "",
//       selectedName: data.file?.name || "",
//       savingsText: savings,
//       compressionProgress: data.compressionProgress || 0,
//       error: data.error || "",
//     });
//   };

//   const handleImageClear = () => {
//     if (ipcState.previewUrl) {
//       URL.revokeObjectURL(ipcState.previewUrl);
//     }
//     setReadyFile(null);
//     setOrigBytes(0);
//     setCompBytes(0);
//     setIpcState({
//       previewUrl: "",
//       selectedName: "",
//       savingsText: "",
//       compressionProgress: 0,
//       error: "",
//     });
//   };

//   const uploadImageAndGetUrl = (fileToUpload) =>
//     new Promise((resolve, reject) => {
//       if (!fileToUpload) return resolve(null);
//       const ts = Date.now();
//       const storageRef = ref(storage, `messages/${mid}/${user.uid}/${ts}_${fileToUpload.name}`);
//       const task = uploadBytesResumable(storageRef, fileToUpload, { contentType: fileToUpload.type });

//       task.on(
//         'state_changed',
//         (snap) => {
//           const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
//           setIpcState(s => ({ ...s, compressionProgress: pct })); // Note: this is upload progress
//         },
//         (err) => reject(err),
//         async () => {
//           const url = await getDownloadURL(task.snapshot.ref);
//           const imagePath = task.snapshot.ref.fullPath;
//           resolve({url, imagePath});
//         }
//       );
//     });

//   const handleSendNewMessage = async () => {
//     const textToSend = newMessage.trim();
//     const fileToUpload = readyFile;
//     const originalBytesValue = origBytes;
//     const compressedBytesValue = compBytes;

//     if (!fileToUpload && !textToSend) {
//       return;
//     }

//     // --- Optimistic UI: Phase 1 ---
//     // Clear input fields immediately.
//     const tempPreviewUrl = ipcState.previewUrl; // Keep preview URL for optimistic display
//     setNewMessage('');
//     handleImageClear(); // This clears readyFile and other UI state

//     try {
//       // Check daily message limit for non-premium users
//       if (!myIsPremium) {
//         const todayStr = new Date().toISOString().slice(0, 10);
//         const counterRef = doc(db, 'dailyMessageCounters', `${user.uid}_${todayStr}`);
//         const snap = await getDoc(counterRef);
//         const count = snap.exists() ? snap.data()?.count || 0 : 0;
//         if (count >= 20) {
//           setHitMsgLimit(true);
//           setNewMessage(textToSend); // Restore text
//           // Note: The image selection is already cleared, which is a limitation.
//           return;
//         }
//       }

//       // Create the message document *immediately* with placeholder content.
//       const payload = {
//         text: textToSend,
//         imageUrl: fileToUpload ? tempPreviewUrl : null, // Use local blob URL for instant display
//         imagePath: null,
//         sender: user.uid,
//         createdAt: serverTimestamp(),
//         read: false,
//         type: fileToUpload ? 'image' : 'text',
//         status: fileToUpload ? 'uploading' : 'sent', // New status field
//         originalBytes: originalBytesValue || null,
//         compressedBytes: compressedBytesValue || null,
//       };

//       // Add the doc and get its reference
//       const messageDocRef = await addDoc(messagesRef, payload);

//       // Update thread's last message optimistically
//       const lastText = `${fileToUpload ? '[Photo]' : ''}${fileToUpload && textToSend ? ' ' : ''}${textToSend}`;
//       await setDoc(
//         threadRef,
//         {
//           lastText: lastText || '[Photo]',
//           updatedAt: serverTimestamp(),
//         },
//         { merge: true }
//       );

//       // Increment daily counter for non-premium users
//       if (!myIsPremium) {
//         const todayStr = new Date().toISOString().slice(0, 10);
//         const counterRef = doc(db, 'dailyMessageCounters', `${user.uid}_${todayStr}`);
//         await setDoc(counterRef, { date: todayStr, count: increment(1) }, { merge: true });
//       }

//       // --- Optimistic UI: Phase 2 ---
//       // If there's a file, upload it in the background.
//       if (fileToUpload) {
//         try {
//           const { url: finalImageUrl, imagePath: finalImagePath } = await uploadImageAndGetUrl(fileToUpload);

//           // Once uploaded, update the message document with the real URL and status.
//           await updateDoc(messageDocRef, {
//             imageUrl: finalImageUrl,
//             imagePath: finalImagePath,
//             status: 'sent',
//           });
//         } catch (uploadError) {
//           console.error("Image upload failed:", uploadError);
//           // If upload fails, update the message to show an error state.
//           await updateDoc(messageDocRef, {
//             status: 'failed',
//             text: `${textToSend} (Image upload failed)`,
//           });
//         } finally {
//             if (tempPreviewUrl) {
//                 URL.revokeObjectURL(tempPreviewUrl);
//             }
//         }
//       }
//     } catch (e) {
//       console.error("An error occurred while sending the message:", e);
//       // If the initial message doc creation fails, restore the text.
//       setNewMessage(textToSend);
//     }
//   };

//   const handleViewUserProfile = () => {
//       navigate(`/profile/${other}`);
//   }

//   const handleDeleteMessage = async () => {
//     if (!messageToDelete) return;

//     // Optimistically close the modal
//     setShowDeleteModal(false);
//     const messageIdToDelete = messageToDelete;
//     setMessageToDelete(null);

//     try {
//       const messageRef = doc(db, 'messages', mid, 'msg', messageIdToDelete);
//       await updateDoc(messageRef, { deleted: true });
//     } catch (error) {
//       console.error("Error deleting message: ", error);
//       // TODO: Handle deletion error, e.g., show a notification
//     }
//   };

//   const openDeleteModal = (messageId) => {
//     setMessageToDelete(messageId);
//     setShowDeleteModal(true);
//   };

//   return (
//     <>
//       <Modal
//         className='upgrade-prompt'
//         isOpen={showDeleteModal}
//         onRequestClose={() => setShowDeleteModal(false)}
//         contentLabel="Delete Message Confirmation"
//       >
//         <h2>Delete Message</h2>
//         <p>Are you sure you want to delete this message? This action cannot be undone.</p>
//         <div className="actions">
//           <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
//           <button onClick={handleDeleteMessage}>Delete</button>
//         </div>
//       </Modal>
//       <Modal
//         className='upgrade-prompt'
//         isOpen={hitMsgLimit}
//         onRequestClose={() => setHitMsgLimit(false)}
//         contentLabel="Upgrade to send more messages"
//       >
//         <h2>Daily message limit reached</h2>
//         <p>You’ve sent 20 messages today. Upgrade to Premium to keep chatting without limits.</p>
//         <div className="actions">
//           <button onClick={() => setHitMsgLimit(false)}>Maybe later</button>
//           <button onClick={() => { setHitMsgLimit(false); navigate('/pricing') }}>Upgrade</button>
//         </div>
//       </Modal>
//       <div className='main conversation'>
//         <div className="recipient-info">
//           <img
//             className="recipient-photo"
//             alt=""
//             src={userByUid[other]?.displayPhoto || undefined}
//             style={{ cursor: 'pointer' }}
//             onClick={handleViewUserProfile}
//           />
//           <p className="recipient-name">{userByUid[other]?.displayName || other}</p>
//         </div>
//         <div className="messages" ref={messagesEndRef}>
//           {messages.map((msg) => {
//             const mine = msg.sender === user?.uid;
//             const senderProfile = userByUid[msg.sender];
//             const senderName = mine ? 'You' : (senderProfile?.displayName || msg.sender);

//             return (
//               <div key={msg.id} className={`message ${mine ? 'outgoing' : 'incoming'}`}>
//                 {myIsPremium && (
//                   <button className="delete-message-btn" onClick={() => openDeleteModal(msg.id)}>
//                     🗑️
//                   </button>
//                 )}
//                 {msg.imageUrl && (
//                   <div>
//                     <a href={msg.imageUrl} target="_blank" rel="noreferrer">
//                       <SafeMedia src={msg.imageUrl} alt="sent" className="message-image" onLoad={scrollToBottom} />
//                     </a>
//                     {msg.expiresAt_sender && msg.sender === user.uid && (
//                       <p className="deletion-date">
//                         Will be deleted on: {new Date(msg.expiresAt_sender.seconds * 1000).toLocaleDateString()}
//                       </p>
//                     )}
//                     {msg.expiresAt_receiver && msg.sender !== user.uid && (
//                       <p className="deletion-date">
//                         Will be deleted on: {new Date(msg.expiresAt_receiver.seconds * 1000).toLocaleDateString()}
//                       </p>
//                     )}
//                   </div>
//                 )}
//                 {msg.text && <div className="message-text" dangerouslySetInnerHTML={{ __html: msg.text }}></div>}
//                 <div className="message-meta">
//                   <span className="sender-name">{senderName}</span>
//                   {mine && (
//                     <div className="message-status">
//                       {msg.read ? 'Seen' : 'Sent'}
//                     </div>
//                   )}
//                   <time
//                     className="timestamp"
//                     dateTime={msg.createdAt?.toDate ? msg.createdAt.toDate().toISOString() : undefined}
//                     title={formatTime(msg.createdAt)}
//                   >
//                     {msg.status === 'uploading' && 'Sending...'}
//                     {msg.status === 'failed' && 'Failed'}
//                     {(!msg.status || msg.status === 'sent') && formatTime(msg.createdAt)}
//                   </time>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       <MessageComposer
//         newMessage={newMessage}
//         setNewMessage={setNewMessage}
//         handleSendNewMessage={handleSendNewMessage}
//         handleImageChange={handleImageChange}
//         ipcState={ipcState}
//         handleImageClear={handleImageClear}
//       />
//     </>
//   );
// }